import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  const isSinglePost = 
    (location.pathname.startsWith('/blog/') && location.pathname.length > 6) || 
    (location.pathname.startsWith('/guides/') && location.pathname.length > 8);

  const headerActive = isScrolled || isSinglePost;

  const getLinkClasses = (path: string) => {
    return `font-medium transition-colors ${
      headerActive
        ? isActive(path)
          ? 'text-brand-coral'
          : 'text-brand-navy hover:text-brand-coral'
        : isActive(path)
          ? 'text-white font-bold underline underline-offset-8'
          : 'text-white hover:text-gray-200'
    }`;
  };

  const getDropdownTriggerClasses = () => {
    return `flex items-center gap-1 font-medium transition-colors cursor-pointer ${
      headerActive 
        ? 'text-brand-navy hover:text-brand-coral' 
        : 'text-white hover:text-gray-200'
    }`;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        headerActive
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Ard's Plumbing & Electric"
              width="1280"
              height="702"
              className="h-12 lg:h-14 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            <Link to="/" className={getLinkClasses('/')}>Home</Link>

            <DropdownMenu>
              <DropdownMenuTrigger className={getDropdownTriggerClasses()}>
                Services <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-0 shadow-xl rounded-xl">
                <DropdownMenuItem asChild><Link to="/services/plumbing" className="cursor-pointer">Plumbing Services</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/services/electrical" className="cursor-pointer">Electrical Services</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/services/residential" className="cursor-pointer">Residential Services</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/services/commercial" className="cursor-pointer">Commercial Services</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={getDropdownTriggerClasses()}>
                Service Areas <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-0 shadow-xl rounded-xl max-h-80 overflow-y-auto">
                {['Dothan', 'Enterprise', 'Ozark', 'Geneva', 'Headland', 'Midland City', 'Daleville', 'Newton', 'Rehobeth'].map((city) => (
                  <DropdownMenuItem asChild key={city}>
                    <Link to={`/service-areas/${city.toLowerCase().replace(' ', '-')}-al`} className="cursor-pointer">
                      {city}, AL
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/about" className={getLinkClasses('/about')}>About</Link>
            <Link to="/reviews" className={getLinkClasses('/reviews')}>Reviews</Link>
            <Link to="/blog" className={getLinkClasses('/blog')}>Blog</Link>
            <Link to="/contact-us" className={getLinkClasses('/contact-us')}>Contact</Link>
          </nav>

          <div className="flex items-center gap-2 lg:gap-4">
            <a
              href="tel:3346925537"
              className={`hidden md:flex items-center gap-2 font-medium transition-colors ${
                headerActive ? 'text-brand-navy hover:text-brand-coral' : 'text-white hover:text-gray-200'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span>(334) 692-5537</span>
            </a>

            <a
              href="https://clienthub.getjobber.com/client_hubs/d4116b0c-e759-4dbf-aeb0-37290a3bcf6e/login/new?source=share_login"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                headerActive
                  ? 'bg-brand-navy text-white hover:bg-brand-blue'
                  : 'bg-black/20 text-white hover:bg-black/30 backdrop-blur-sm'
              }`}
            >
              <User className="w-4 h-4" />
              <span className="hidden xl:inline">Client Login</span>
            </a>

            <Button asChild className="bg-brand-coral hover:bg-brand-coral/90 text-white font-semibold rounded-full px-4 lg:px-6 shadow-sm">
              <Link to="/contact-us">Book Online</Link>
            </Button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close mobile menu" : "Open mobile menu"}
              aria-expanded={isMobileMenuOpen}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                headerActive ? 'text-brand-navy' : 'text-white'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 overflow-y-auto max-h-[calc(100vh-64px)] ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="flex flex-col p-4 space-y-2 pb-20">
          <Link to="/" className="py-3 px-4 rounded-lg hover:bg-gray-100 text-brand-navy font-medium">Home</Link>
          
          <div className="py-2 px-4 text-brand-navy font-semibold text-sm uppercase tracking-wider">Services</div>
          <Link to="/services/plumbing" className="py-2 px-8 rounded-lg hover:bg-gray-100 text-gray-700">Plumbing Services</Link>
          <Link to="/services/electrical" className="py-2 px-8 rounded-lg hover:bg-gray-100 text-gray-700">Electrical Services</Link>
          <Link to="/services/residential" className="py-2 px-8 rounded-lg hover:bg-gray-100 text-gray-700">Residential Services</Link>
          <Link to="/services/commercial" className="py-2 px-8 rounded-lg hover:bg-gray-100 text-gray-700">Commercial Services</Link>
          
          <div className="py-2 px-4 text-brand-navy font-semibold text-sm uppercase tracking-wider">Service Areas</div>
          {['Dothan', 'Enterprise', 'Ozark', 'Geneva', 'Headland', 'Midland City', 'Daleville', 'Newton', 'Rehobeth'].map((city) => (
             <Link key={city} to={`/service-areas/${city.toLowerCase().replace(' ', '-')}-al`} className="py-2 px-8 rounded-lg hover:bg-gray-100 text-gray-700">{city}, AL</Link>
          ))}
          
          <div className="border-t border-gray-100 my-2"></div>
          
          <Link to="/about" className="py-3 px-4 rounded-lg hover:bg-gray-100 text-brand-navy font-medium">About</Link>
          <Link to="/reviews" className="py-3 px-4 rounded-lg hover:bg-gray-100 text-brand-navy font-medium">Reviews</Link>
          <Link to="/blog" className="py-3 px-4 rounded-lg hover:bg-gray-100 text-brand-navy font-medium">Blog</Link>
          <Link to="/contact-us" className="py-3 px-4 rounded-lg hover:bg-gray-100 text-brand-navy font-medium">Contact</Link>
          
          <div className="pt-4 space-y-3">
            <a
              href="https://clienthub.getjobber.com/client_hubs/d4116b0c-e759-4dbf-aeb0-37290a3bcf6e/login/new?source=share_login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-brand-navy text-white font-medium"
            >
              <User className="w-4 h-4" /> Client Login
            </a>
            <a
              href="tel:3346925537"
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-brand-coral text-white font-medium"
            >
              <Phone className="w-4 h-4" /> Call (334) 692-5537
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;